// src/components/Sidebar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    LogOut, Clock, FileText, ChevronDown, ChevronUp
} from 'lucide-react';

const MenuItem = ({ icon: Icon, label, onClick, isActive, hasSubmenu = false, isSubmenuOpen = false }) => (
    <button onClick={onClick} style={{ ...sidebarStyles.menuItem, ...(isActive ? sidebarStyles.menuItemActive : {}) }}>
        <Icon size={20} style={sidebarStyles.menuIcon} />
        <span style={sidebarStyles.menuLabel}>{label}</span>
        {hasSubmenu && (isSubmenuOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
    </button>
);

const SubMenuItem = ({ label, onClick, isActive }) => (
    <button onClick={onClick} style={{ ...sidebarStyles.submenuItem, ...(isActive ? sidebarStyles.submenuItemActive : {}) }}>
        {label}
    </button>
);

export default function Sidebar({ isMobile, isMobileMenuOpen, closeMobileMenu }) {
    const navigate = useNavigate();
    const location = useLocation();
    const sidebarRef = useRef(null);
    const overlayRef = useRef(null);

    const [openSections, setOpenSections] = useState({
        geral: true,
        relatorios: false,
    });

    const toggleSection = (sectionName) => {
        setOpenSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
    };

    const navigateTo = (route) => {
        if (isMobile) closeMobileMenu();
        navigate(route);
    };

    const handleLogout = () => {
        if (window.confirm("Tem certeza de que deseja sair?")) {
            localStorage.removeItem('token');
            window.location.href = "/";
        }
    };

    const sidebarClasses = [
        'sidebar',
        isMobile ? 'mobile' : 'desktop',
        isMobile && isMobileMenuOpen ? 'open' : '',
    ].join(' ');

    useEffect(() => {
        document.body.style.overflow = isMobile && isMobileMenuOpen ? 'hidden' : 'auto';
        return () => { document.body.style.overflow = 'auto'; };
    }, [isMobile, isMobileMenuOpen]);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
    console.log("pathname atual:", location.pathname);
    console.log("isActive('/trajetos'):", isActive('/trajetos'));
    const isSectionActive = (paths) => paths.some(path => isActive(path));

    return (
        <>
            {isMobile && (
                <div
                    ref={overlayRef}
                    onClick={closeMobileMenu}
                    style={{ ...sidebarStyles.overlay, display: isMobileMenuOpen ? 'block' : 'none' }}
                />
            )}

            <aside ref={sidebarRef} className={sidebarClasses} style={sidebarStyles.base}>
                <div style={sidebarStyles.header}>
                    <span style={sidebarStyles.headerTitle}>SISTEMA DE RASTREAMENTO</span>
                </div>

                <nav style={sidebarStyles.nav}>
                    <MenuItem
                        icon={Clock}
                        label="Geral"
                        hasSubmenu
                        isSubmenuOpen={openSections.geral}
                        onClick={() => toggleSection('geral')}
                    />
                    {openSections.geral && (
                        <div style={sidebarStyles.submenu}>
                            <SubMenuItem label="Início" onClick={() => navigateTo('/inicio')} isActive={isActive('/inicio')} />
                            <SubMenuItem label="Localização" onClick={() => navigateTo('/localizacao')} isActive={isActive('/localizacao')} />
                            <SubMenuItem label="Trajetos" onClick={() => navigateTo('/trajetos')} isActive={isActive('/trajetos')} />
                            <SubMenuItem label="Últimos registros" onClick={() => navigateTo('/ultimos-registros')} isActive={isActive('/ultimos-registros')} />
                        </div>
                    )}

                    <MenuItem
                        icon={FileText}
                        label="Relatórios"
                        hasSubmenu
                        isSubmenuOpen={openSections.relatorios}
                        isActive={isSectionActive([
                            '/relatorios/deslocamentos',
                            '/relatorios/velocidade',
                            '/relatorios/assistente'
                        ])}
                        onClick={() => toggleSection('relatorios')}
                    />
                    {openSections.relatorios && (
                        <div style={sidebarStyles.submenu}>
                            <SubMenuItem label="Deslocamentos e paradas" onClick={() => navigateTo('/relatorios/deslocamentos')} isActive={isActive('/relatorios/deslocamentos')} />
                            <SubMenuItem label="Velocidade" onClick={() => navigateTo('/relatorios/velocidade')} isActive={isActive('/relatorios/velocidade')} />
                            <SubMenuItem label="Assistente" onClick={() => navigateTo('/relatorios/assistente')} isActive={isActive('/relatorios/assistente')} />
                        </div>
                    )}
                </nav>

                <div style={sidebarStyles.footer}>
                    <button onClick={handleLogout} style={sidebarStyles.logoutButton}>
                        <LogOut size={20} />
                        Sair
                    </button>
                </div>
            </aside>

            <style jsx global>{`
                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 240px;
                    height: 100%;
                    background-color: #2C2C2C;
                    color: #e0e0e0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    z-index: 1000;
                    transition: transform 0.3s ease-in-out;
                    box-shadow: 2px 0px 10px rgba(0,0,0,0.2);
                }

                .sidebar.desktop {
                    transform: translateX(0%);
                    padding-top: 65px;
                    height: calc(100% - 65px);
                    top: 65px;
                }

                .sidebar.mobile {
                    width: 80%;
                    max-width: 300px;
                    transform: translateX(-100%);
                    z-index: 1100;
                    top: 0;
                    height: 100%;
                    padding-top: 0;
                }

                .sidebar.mobile.open {
                    transform: translateX(0%);
                }
            `}</style>
        </>
    );
}

const sidebarStyles = {
    base: {
        backgroundColor: '#2C2C2C',
        color: '#e0e0e0',
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s ease-in-out',
    },
    header: {
        padding: '15px 20px',
        borderBottom: '1px solid #444',
        marginBottom: '10px',
    },
    headerTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#a0a0a0',
        textTransform: 'uppercase',
    },
    nav: {
        flexGrow: 1,
        overflowY: 'auto',
        padding: '0 10px',
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '12px 10px',
        background: 'none',
        border: 'none',
        color: '#e0e0e0',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: '15px',
        borderRadius: '6px',
        marginBottom: '5px',
        transition: 'background-color 0.2s, color 0.2s',
    },
    menuItemActive: {
        /*backgroundColor: '#444',
        color: '#ffffff',
        fontWeight: '500',*/
    },
    menuIcon: {
        marginRight: '12px',
        flexShrink: 0,
    },
    menuLabel: {
        flexGrow: 1,
    },
    submenu: {
        paddingLeft: '22px',
        marginTop: '5px',
        marginBottom: '5px',
    },
    submenuItem: {
        display: 'block',
        width: '100%',
        padding: '8px 10px 8px 20px',
        background: 'none',
        border: 'none',
        color: '#c0c0c0',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: '14px',
        borderRadius: '4px',
        marginBottom: '2px',
        transition: 'background-color 0.2s, color 0.2s',
    },
    submenuItemActive: {
        /*backgroundColor: '#555',
        color: '#ffffff',
        fontWeight: '500',*/
    },
    footer: {
        padding: '15px 20px',
        marginTop: 'auto',
        borderTop: '1px solid #444',
    },
    logoutButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '10px',
        backgroundColor: 'transparent',
        color: '#e65050',
        padding: '10px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left',
        fontSize: '15px',
        fontWeight: '500',
        transition: 'background-color 0.2s, color 0.2s',
    },
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1050,
    },
};
